import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  TextField,
  Modal,
  Stack,
  Alert,
  Tooltip,
} from "@mui/material";

const API_BASE = "/api/bdb/pet";

const dogGif =
  "https://res.cloudinary.com/dbav8xw6s/image/upload/v1665612860/hueso/assets/RECOMPENSA/HUESOOROANIMADO_wpjjpv.gif";

/*
  Cambia dogGif luego por tu perro realista:
  idle.webp
  walk.webp
  eat.webp
  drink.webp
*/

function getToken() {
  return sessionStorage.getItem("token");
}

function petApi() {
  const token = getToken();

  return axios.create({
    baseURL: API_BASE,
    headers: {
      token,
    },
  });
}

function StatBar({ label, value }) {
  const safeValue = Math.max(0, Math.min(100, Number(value || 0)));

  return (
    <Box sx={{ width: "100%", mb: 1 }}>
      <Typography sx={{ color: "white", fontSize: 12 }}>
        {label}: {Math.round(safeValue)}%
      </Typography>

      <LinearProgress
        variant="determinate"
        value={safeValue}
        sx={{
          height: 8,
          borderRadius: 2,
          backgroundColor: "rgba(255,255,255,0.2)",
          "& .MuiLinearProgress-bar": {
            backgroundColor:
              safeValue > 60 ? "#00e676" : safeValue > 30 ? "#ffb300" : "#ff1744",
          },
        }}
      />
    </Box>
  );
}

export default function PetOverlay() {
  const [petData, setPetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openPanel, setOpenPanel] = useState(false);
  const [nickname, setNickname] = useState("");
  const [message, setMessage] = useState(null);
  const [animation, setAnimation] = useState("idle");

  const pet = petData?.pet;
  const cityFarm = petData?.cityFarm;
  const bdb = petData?.bdb;
  const benefit = petData?.benefit;

  const displayName = useMemo(() => {
    if (!pet) return "My Puppy";
    return pet.nickname || pet.birthName || "My Puppy";
  }, [pet]);

  const loadPet = async () => {
    try {
      setLoading(true);

      const res = await petApi().get("/me");

      setPetData(res.data);
      setNickname(res.data?.pet?.nickname || "");
    } catch (err) {
      console.error("[PET FRONT] Error cargando mascota:", err);

      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "No se pudo cargar la mascota.",
      });
    } finally {
      setLoading(false);
    }
  };

  const doAction = async (action) => {
    try {
      setLoading(true);
      setMessage(null);

      if (action === "feed") setAnimation("eat");
      if (action === "water") setAnimation("drink");
      if (action === "play") setAnimation("play");

      const res = await petApi().post(`/${action}`);

      setPetData((prev) => ({
        ...prev,
        ...res.data,
        pet: res.data.pet,
        benefit: res.data.benefit || prev?.benefit,
        cityFarm: {
          ...(prev?.cityFarm || {}),
          ...(res.data.cityFarm || {}),
        },
      }));

      setMessage({
        type: "success",
        text: res.data?.message || "Acción realizada.",
      });

      setTimeout(() => setAnimation("idle"), 2500);
    } catch (err) {
      console.error("[PET FRONT] Error acción:", err);

      setAnimation("idle");

      const data = err?.response?.data;

      setMessage({
        type: "error",
        text:
          data?.cooldown?.text
            ? `${data?.message} Espera ${data.cooldown.text}`
            : data?.message || data?.error || "No se pudo realizar la acción.",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveNickname = async () => {
    try {
      if (!nickname || nickname.trim().length < 2) {
        setMessage({
          type: "error",
          text: "El sobrenombre debe tener mínimo 2 caracteres.",
        });
        return;
      }

      setLoading(true);

      const res = await petApi().post("/nickname", {
        nickname,
      });

      setPetData((prev) => ({
        ...prev,
        pet: res.data.pet,
      }));

      setMessage({
        type: "success",
        text: "Sobrenombre actualizado.",
      });
    } catch (err) {
      console.error("[PET FRONT] Error nickname:", err);

      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "No se pudo cambiar el sobrenombre.",
      });
    } finally {
      setLoading(false);
    }
  };

  const revivePet = async (paymentType) => {
    try {
      setLoading(true);
      setMessage(null);

      const res = await petApi().post("/revive", {
        paymentType,
      });

      setPetData((prev) => ({
        ...prev,
        pet: res.data.pet,
        bdb: {
          ...(prev?.bdb || {}),
          ...(res.data.bdb || {}),
        },
      }));

      setMessage({
        type: "success",
        text: res.data?.message || "Cachorro reactivado.",
      });
    } catch (err) {
      console.error("[PET FRONT] Error revive:", err);

      setMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "No se pudo reactivar el cachorro.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPet();

    const interval = setInterval(() => {
      loadPet();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  if (!pet) {
    return null;
  }

  return (
    <>
      {/* Mascota caminando/flotante */}
      <Tooltip title="Click para ver tu cachorro">
        <Box
          onClick={() => setOpenPanel(true)}
          sx={{
            position: "fixed",
            left: "35px",
            bottom: "70px",
            zIndex: 9999,
            cursor: "pointer",
            userSelect: "none",
            animation:
              animation === "idle"
                ? "petWalk 8s linear infinite alternate"
                : "petBounce 0.6s ease-in-out infinite alternate",
            "@keyframes petWalk": {
              "0%": { transform: "translateX(0px)" },
              "100%": { transform: "translateX(180px)" },
            },
            "@keyframes petBounce": {
              "0%": { transform: "translateY(0px)" },
              "100%": { transform: "translateY(-10px)" },
            },
          }}
        >
          <Box
            sx={{
              background: "rgba(0,0,0,0.65)",
              color: "white",
              border: "1px solid rgba(255,153,0,0.8)",
              borderRadius: "14px",
              px: 1,
              py: 0.5,
              mb: 0.5,
              fontSize: 12,
              textAlign: "center",
              maxWidth: 180,
            }}
          >
            {pet.status === "fallen"
              ? "💀 Necesito ayuda"
              : animation === "eat"
              ? "🍖 Ñam ñam"
              : animation === "drink"
              ? "💧 Tomando agua"
              : animation === "play"
              ? "🎾 Jugando"
              : `🐶 ${displayName}`}
          </Box>

          <Box
            component="img"
            src={dogGif}
            alt="Big Dog Bone Pet"
            sx={{
              width: 95,
              height: 95,
              objectFit: "contain",
              filter:
                pet.status === "fallen"
                  ? "grayscale(1) brightness(0.5)"
                  : "drop-shadow(0 0 10px rgba(255,153,0,0.9))",
            }}
          />
        </Box>
      </Tooltip>

      {/* Panel */}
      <Modal open={openPanel} onClose={() => setOpenPanel(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 430 },
            maxHeight: "90vh",
            overflowY: "auto",
            background: "linear-gradient(180deg, #1f1f1f, #080808)",
            border: "2px solid #ff9800",
            borderRadius: "18px",
            boxShadow: "0 0 30px rgba(255,152,0,0.5)",
            p: 3,
            color: "white",
          }}
        >
          <Typography variant="h5" sx={{ color: "#ffb300", mb: 1 }}>
            🐶 Big Dog Bone Puppy
          </Typography>

          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <Box
              component="img"
              src={dogGif}
              alt="Pet"
              sx={{
                width: 95,
                height: 95,
                objectFit: "contain",
                filter:
                  pet.status === "fallen"
                    ? "grayscale(1) brightness(0.5)"
                    : "drop-shadow(0 0 10px rgba(255,153,0,0.8))",
              }}
            />

            <Box>
              <Typography>
                <b>Nombre nacimiento:</b>
              </Typography>
              <Typography sx={{ color: "#ffcc80", fontSize: 13 }}>
                {pet.birthName}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                <b>Sobrenombre:</b> {pet.nickname || "Sin sobrenombre"}
              </Typography>

              <Typography>
                <b>Etapa:</b> {pet.stage}
              </Typography>

              <Typography>
                <b>Estado:</b>{" "}
                <span
                  style={{
                    color:
                      pet.status === "alive"
                        ? "#00e676"
                        : pet.status === "weak"
                        ? "#ffb300"
                        : "#ff1744",
                  }}
                >
                  {pet.status}
                </span>
              </Typography>

              <Typography>
                <b>Nivel:</b> {pet.level} | <b>XP:</b> {pet.xp}
              </Typography>
            </Box>
          </Box>

          <StatBar label="Vida" value={pet.life?.current || 0} />
          <StatBar label="Hambre" value={pet.hunger} />
          <StatBar label="Sed" value={pet.thirst} />
          <StatBar label="Felicidad" value={pet.happiness} />
          <StatBar label="Salud" value={pet.health} />
          <StatBar label="Energía" value={pet.energy} />

          <Box
            sx={{
              background: "rgba(255,255,255,0.06)",
              borderRadius: 2,
              p: 1.5,
              mt: 2,
              mb: 2,
            }}
          >
            <Typography sx={{ color: "#ffb300", mb: 1 }}>
              Recursos CityFarm
            </Typography>

            <Typography>
              💧 Water: {cityFarm?.inventory?.Water ?? 0}
            </Typography>

            <Typography>
              🍖 Croquettes: {cityFarm?.inventory?.Croquettes ?? 0}
            </Typography>
          </Box>

          <Box
            sx={{
              background: "rgba(255,255,255,0.06)",
              borderRadius: 2,
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography sx={{ color: "#ffb300", mb: 1 }}>
              Beneficio
            </Typography>

            <Typography>
              Estado: {benefit?.active ? "✅ Activo" : "❌ Inactivo"}
            </Typography>

            <Typography>
              Mini-Hueso diario: {benefit?.dailyClaim?.miniHueso || 0}
            </Typography>
          </Box>

          <Stack spacing={1.2}>
            {pet.status !== "fallen" ? (
              <>
                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={() => doAction("feed")}
                  sx={{ backgroundColor: "#ff9800" }}
                >
                  🍖 Dar Croquettes
                </Button>

                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={() => doAction("water")}
                  sx={{ backgroundColor: "#2196f3" }}
                >
                  💧 Dar Water
                </Button>

                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={() => doAction("play")}
                  sx={{ backgroundColor: "#4caf50" }}
                >
                  🎾 Jugar
                </Button>

                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={() => doAction("claim-daily")}
                  sx={{ backgroundColor: "#9c27b0" }}
                >
                  🦴 Reclamar beneficio diario
                </Button>
              </>
            ) : (
              <>
                <Alert severity="error">
                  Tu cachorro está caído. Puedes reactivarlo con mini-hueso o HUESO real.
                </Alert>

                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={() => revivePet("dinero")}
                  sx={{ backgroundColor: "#ff9800" }}
                >
                  Reactivar con mini-hueso
                </Button>

                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={() => revivePet("dineroReal")}
                  sx={{ backgroundColor: "#f44336" }}
                >
                  Reactivar con HUESO real
                </Button>
              </>
            )}

            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Sobrenombre"
                sx={{
                  input: { color: "white" },
                  label: { color: "white" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#ff9800" },
                  },
                }}
              />

              <Button
                disabled={loading}
                variant="outlined"
                onClick={saveNickname}
                sx={{ color: "#ffb300", borderColor: "#ffb300" }}
              >
                Guardar
              </Button>
            </Box>

            <Button
              variant="text"
              onClick={loadPet}
              sx={{ color: "#ffcc80" }}
            >
              Actualizar
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
}